import React from 'react';
import { useLocation } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';

import chartColors from 'scenes/Producer/Dashboard/chartOptions';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import { HttpStatus } from 'v2/interfaces/http';
import { ResolveSubscriptionChangeStatus } from 'v2/interfaces/subscriptions';
import type { ResolveSubscriptionChangeResponse } from 'v2/interfaces/subscriptions';
import subscriptionsService from 'v2/services/subscriptions';

import { Topbar } from '@eduzz/ui-layout';

type MessageType = {
  icon: React.ReactNode;
  description: string;
};

const iconSize = 72;

export default function ResolveRecurringDateChange() {
  return (
    <div>
      <Topbar />
      <ResolveRecurringDateChangeResultSection />
    </div>
  );
}

function ResolveRecurringDateChangeResultSection() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const token = params.get('token');
  const action = params.get('action');

  const response = useObservable(() => {
    return subscriptionsService.resolveRecurringDateChange(token, action);
  }, []);

  const [body, error] = response;

  if (!body && !error) {
    return <GlobalLoading />;
  }

  if (error?.response?.status === HttpStatus.UnprocessableEntity) {
    return (
      <MessageContainer
        icon={<WarningOutlined style={{ fontSize: iconSize, color: chartColors.yellow.background }} />}
        description='Este link já foi utilizado portanto não é mais válido.'
      />
    );
  }

  if (error) {
    return (
      <MessageContainer
        icon={<WarningOutlined style={{ fontSize: iconSize, color: chartColors.yellow.background }} />}
        description='Ocorreu um erro ao processar a solicitação. Por favor, tente novamente mais tarde.'
      />
    );
  }

  const messageMapper: Record<ResolveSubscriptionChangeResponse['data']['status'], MessageType> = {
    [ResolveSubscriptionChangeStatus.EXPIRED]: {
      icon: <WarningOutlined style={{ fontSize: iconSize, color: chartColors.yellow.background }} />,
      description:
        'Este link expirou. O prazo de resposta de 48 horas foi ultrapassado. Para alterar a data de vencimento, entre em contato com o produtor.'
    },
    [ResolveSubscriptionChangeStatus.APPROVED]: {
      icon: <CheckCircleOutlined style={{ fontSize: iconSize, color: chartColors.green.background }} />,
      description:
        'Solicitação confirmada! A nova data de vencimento será atualizada no seu contrato conforme solicitado.'
    },
    [ResolveSubscriptionChangeStatus.REJECTED]: {
      icon: <CloseCircleOutlined style={{ fontSize: iconSize, color: chartColors.red.background }} />,
      description: 'Alteração recusada. Sua data de vencimento continuará a mesma.'
    }
  };

  const message = messageMapper[body.data.status] || {
    icon: <WarningOutlined style={{ fontSize: iconSize, color: chartColors.yellow.background }} />,
    description: 'Algo deu errado. Por favor, tente novamente mais tarde.'
  };

  return <MessageContainer icon={message.icon} description={message.description} />;
}

function MessageContainer({ icon, description }: MessageType) {
  return (
    <div className='flex h-[calc(100vh-64px)] w-full flex-col items-center justify-center p-4 text-2xl'>
      <div className='flex w-full max-w-[500px] flex-col items-center justify-center text-center'>
        <span>{icon}</span>
        <span>{description}</span>
      </div>
    </div>
  );
}
